import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import Modal from '@tds/community-modal';
import { useHistory } from 'react-router-dom';

import { Error } from '../design/elements';
import FormField from './formField';
import { EquipmentSectionSubTitle, EquipmentSectionTitle, EquipmentSectionWrapper } from './css';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import stringInterpolation from '../utils/stringInterpolation';
import { getFieldValue } from './utils';
import {
  cameraTypeList,
  controlIncludedCameraLimit,
  controlIncludedPaid2TbSvr,
  controlIncludedSdCard,
  easyFiveYearIndoorCamera,
  easyFiveYearMicroSdCard128Gb,
  easyFiveYearOutdoorCamera,
  easyFiveYearPoeTurretCamera,
  easyFiveYearTemperatureProbe,
  easyFiveYearTemperatureSensor,
  easyIndoorCamera,
  easyMicroSdCard128Gb,
  easyOutdoorCamera,
  easyPoeSwitchNonZero,
  easyPoeSwitchZero,
  easyPoeTurret,
  easyPoeDomeCamera,
  easyTemperatureProbe,
  easyTemperatureSensor,
  fieldEasyFiveYearFivePortPoeSwitch,
  fieldEasyFiveYearFivePortPoeSwitchPaid,
  fieldEasyPoeSwitchNonZero,
  fieldEasyPoeSwitchZero,
  fieldPoeSwitchIncludedEquipment,
  fieldPoeSwitchNonZero,
  fieldPoeSwitchZero,
  indoorCamera,
  indoorCameraFree,
  keyControlAdditionalEquipment,
  keyControlIncludedEquipmentSelection,
  keyMonitorAdditionalEquipment,
  keyProtectAdditionalEquipment,
  keyProtectIncludedEquipmentSelection,
  keySecurityCameraEquipment,
  keySmartCameraIncludedEquipment,
  microSdCard128Gb,
  outdoorCamera,
  outdoorCameraFree,
  poeSwitchIncludedEquipment,
  poeSwitchNonZero,
  poeSwitchZero,
  poeTurretCamera,
  poeTurretCameraFree,
  poeDomeCamera,
  poeDomeCameraFree,
  secureBusinessMaxCameraLimit,
  secureBusinessOppositeSectionKeys,
  temperatureProbe,
  temperatureSensor,
  typePoeCamera,
  typeSwitch
} from '../utils/constant';

const EquipmentSectionForm = ({
  data,
  formErrors,
  formFields,
  setFormFields,
  isFormTouched,
  setIsFormTouched,
  equipmentTotalPrice,
  setEquipmentTotalPrice,
  hardwareDiscountAmount,
  setHardwareDiscountAmount,
  dueNow,
  setDueNow,
  isHardwareDiscount,
  renderTempSensor,
  setRenderTempSensor
}) => {
  const [isAlert, setIsAlertOpen] = useState(false);
  const [bodyText,setBodyText]=useState('');
  // @Todo: why do we need the tempSensorPrice variable? It is not used.
  const [tempSensorPrice,setTempSensorPrice]= useState({
    temperatureMonitoringFiveDollar:0,
    temperatureMonitoringTenDollar:0
  });

  const {
    name,
    title,
    subTitle,
    maxValue,
    isLimitCameraOnly,
    formStepFields,
    oneTimePaymentTitle,
    easyPaymentTitle,
    easyPaymentFields,
    fiveYearEasyPaymentFields,
    fiveYearEasyPaymentTitle
  } = data;

  let finalFields;
  if (Array.isArray(formStepFields) && Array.isArray(easyPaymentFields) && Array.isArray(fiveYearEasyPaymentFields)) {
    finalFields = [...formStepFields, ...easyPaymentFields, ...fiveYearEasyPaymentFields];
  } else {
    finalFields = formStepFields;
  }

  const radioSelection = name.slice(0, 7);
  const getObjectValue = (obj, path) => _.get(obj, `${path}.value`, 0);
  const setObjectValue = (obj, path, value, key = 'value') => _.set(obj, `${path}.${key}`, value);
  const localizedStrings = useLocalizedStringsContext();
  const {
    fieldPlusMinus: { errorMessage },
    secureBusiness: { maxCameraLimitErrorMessage }
  } = localizedStrings;

  const checkIfHasSwitchOption = () => {
    const isSwitch =
      formFields[name] &&
      (Object.prototype.hasOwnProperty.call(formFields[name], fieldPoeSwitchZero) ||
        Object.prototype.hasOwnProperty.call(formFields[name], fieldPoeSwitchNonZero) ||
        Object.prototype.hasOwnProperty.call(formFields[name], fieldPoeSwitchIncludedEquipment) ||
        Object.prototype.hasOwnProperty.call(formFields[name], temperatureProbe) ||
        Object.prototype.hasOwnProperty.call(formFields[name], fieldEasyPoeSwitchZero) ||
        Object.prototype.hasOwnProperty.call(formFields[name], fieldEasyPoeSwitchNonZero) ||
        Object.prototype.hasOwnProperty.call(formFields[name], easyTemperatureProbe) ||
        Object.prototype.hasOwnProperty.call(formFields[name], fieldEasyFiveYearFivePortPoeSwitch) ||
        Object.prototype.hasOwnProperty.call(formFields[name], fieldEasyFiveYearFivePortPoeSwitchPaid) ||
        Object.prototype.hasOwnProperty.call(formFields[name], easyFiveYearTemperatureProbe)
      );
    return isSwitch;
  };

  const formObjectKeys = useMemo(
    () =>
      finalFields.reduce((acc, { fields: { name: formName } }) => {
        acc.push(formName);
        return acc;
      }, []),
    [formStepFields]
  );

  useEffect(() => {
    // Calc initial prices
    const fields = { ...formFields[name] } || {};
    if (fields) {
      const sumForSection = formObjectKeys
        .filter(kf => (fields[kf] || {}).isVisible)
        .reduce((acc, kn) => {
          const val =
            parseInt((fields[kn] || {}).value || 0, 10) *
            (fields[kn] || {}).price || 0;
          return acc + val;
        }, 0);
      const sumForMonthly = formObjectKeys
        .filter(kf => (fields[kf] || {}).isVisible)
        .reduce((acc, kn) => {
          const val =
            parseInt((fields[kn] || {}).value || 0, 10) *
            (fields[kn] || {}).monthlyPayment || 0;
          return acc + val;
        }, 0);

      const sumForFiveYearMonthly = formObjectKeys
        .filter(kf => (fields[kf] || {}).isVisible)
        .reduce((acc, kn) => {
          const val =
            parseInt((fields[kn] || {}).value || 0, 10) *
            (fields[kn] || {}).fiveYearEasyPayment || 0;
          return acc + val;
        }, 0);

      const currentPrice = equipmentTotalPrice.addAmountToPrice;
      const newPrice = currentPrice + sumForSection;
      const currentMonthlyPayment = equipmentTotalPrice.addAmountToMonthly;
      let newMonthlyPayment = currentMonthlyPayment + sumForMonthly ;
      const currentFiveYearMonthly = equipmentTotalPrice.addAmountToFiveYearMonthly;
      const newFiveYearMonthly = currentFiveYearMonthly + sumForFiveYearMonthly ;
      // SOF-1768
      const valueTempSensor = getFieldValue(
        temperatureSensor,
        fields
      );
      if (valueTempSensor > 0) {
        newMonthlyPayment += 5;
      }
      if (valueTempSensor > 5) {
        newMonthlyPayment += 5;
      }
      const valueEasyTempSensor = getFieldValue(
        easyTemperatureSensor,
        fields
      );
      if (valueEasyTempSensor > 0) {
        newMonthlyPayment += 5;
      }
      if (valueEasyTempSensor > 5) {
        newMonthlyPayment += 5;
      }
      if (valueTempSensor > 0 && valueEasyTempSensor > 0) {
        newMonthlyPayment -= 5;
      }
      if (
        [
          keyControlAdditionalEquipment,
          keyProtectAdditionalEquipment,
          keyMonitorAdditionalEquipment
        ].includes(name)
      ) {
        let discountAmount = 0;
        if (newPrice >= 200) {
          discountAmount = 200;
        } else if (newPrice > 0) {
          discountAmount = newPrice;
        }
        if (isHardwareDiscount) setDueNow(dueNow + hardwareDiscountAmount);
        setHardwareDiscountAmount(discountAmount);
      }
      setEquipmentTotalPrice({
        subtractAmountFromPrice: currentPrice,
        addAmountToPrice: newPrice,
        subtractAmountFromMonthly: currentMonthlyPayment,
        addAmountToMonthly: newMonthlyPayment ,
        addAmountToFiveYearMonthly : newFiveYearMonthly,
        subtractAmountFromFiveYearMonthly : currentFiveYearMonthly
      });
    }
  }, []);

  const checkIfTotalOverLimit = (opt, fieldProductType) => {
    const returnObj = {
      totalOverLimit: false,
      isCameraLimit: false
    };

    if ([
        keySmartCameraIncludedEquipment,
        keySecurityCameraEquipment,
        keyProtectIncludedEquipmentSelection,
        keyProtectAdditionalEquipment,
        keyControlIncludedEquipmentSelection,
        keyControlAdditionalEquipment,
        keyMonitorAdditionalEquipment
      ].includes(name)
      && cameraTypeList.includes(fieldProductType)) {
      const otherSectionKey = secureBusinessOppositeSectionKeys[name];
      const otherSection = { ...formFields[otherSectionKey] };
      const otherSectionSum = Object.keys(otherSection)
        .filter(ko =>
          cameraTypeList.includes((otherSection[ko] || {}).productType)
        )
        .reduce(
          (acc, ko) => acc + parseInt(otherSection[ko].value || 0, 10),
          0
        );
      const currentSum = formObjectKeys
        .filter(key => cameraTypeList.includes(opt[key].productType))
        .reduce((acc, key) => acc + parseInt(opt[key].value || 0, 10), 0);

      if (otherSectionSum + currentSum > secureBusinessMaxCameraLimit) {
        returnObj.totalOverLimit = true;
        returnObj.isCameraLimit = true;
        const text = stringInterpolation(
          returnObj.isCameraLimit ? maxCameraLimitErrorMessage : errorMessage,
          returnObj.isCameraLimit ? secureBusinessMaxCameraLimit : maxValue
        );
        setIsAlertOpen(true);
        setBodyText(text);
        return returnObj;
      }
      if ([
          keyProtectIncludedEquipmentSelection,
          keyControlIncludedEquipmentSelection
        ].includes(name)
        && typeof maxValue === 'number') {
        const sectionSum = formObjectKeys
          .filter(k => opt[k].productType !== typeSwitch)
          .reduce((acc, k) => acc + parseInt(opt[k].value || 0, 10), 0);
        if (sectionSum > maxValue) {
          returnObj.totalOverLimit = true;
          returnObj.isCameraLimit = false;
          const text = stringInterpolation(
            returnObj.isCameraLimit ? maxCameraLimitErrorMessage : errorMessage,
            returnObj.isCameraLimit ? secureBusinessMaxCameraLimit : maxValue
          );
          setIsAlertOpen(true);
          setBodyText(text);
          return returnObj;
        }
      }
    } else if (typeof maxValue === 'number' && !isLimitCameraOnly) {
      const currentSum = formObjectKeys
        .filter(k => opt[k].productType !== typeSwitch)
        .reduce((acc, k) => acc + parseInt(opt[k].value || 0, 10), 0);
      if (currentSum > maxValue) {
        returnObj.totalOverLimit = true;
        returnObj.isCameraLimit = false;
        const text = stringInterpolation(
          returnObj.isCameraLimit ? maxCameraLimitErrorMessage : errorMessage,
          returnObj.isCameraLimit ? secureBusinessMaxCameraLimit : maxValue
        );
        setIsAlertOpen(true);
        setBodyText(text);
        return returnObj;
      }
    }
    if (
      [
        keyProtectAdditionalEquipment,
        keyControlAdditionalEquipment,
        keyMonitorAdditionalEquipment
      ].includes(name) && fieldProductType === 'Probe') {
      const otherSectionKey = secureBusinessOppositeSectionKeys[name];
      const otherSection = { ...formFields[otherSectionKey] };
      const otherSectionSum = Object.keys(otherSection)
        .filter(ko =>
          'Probe'.includes((otherSection[ko] || {}).productType)
        )
        .reduce(
          (acc, ko) => acc + parseInt(otherSection[ko].value || 0, 10),
          0
        );
      const currentSum = formObjectKeys
        .filter(key => 'Probe'.includes(opt[key].productType))
        .reduce((acc, key) => acc + parseInt(opt[key].value || 0, 10), 0);
      if (otherSectionSum + currentSum > 10) {
        returnObj.totalOverLimit = true;
        returnObj.isCameraLimit = true;
        const text = stringInterpolation(
          "A maximum of %s temerature sensors can be selected in total",
          10
        );
        setIsAlertOpen(true);
        setBodyText(text);
        return returnObj;
      }
    }

    return returnObj;
  };

  const getControlIncludedCameraTotal = (opt, fieldProductType) => {
    const returnObj = {
      controlIncludedCameraTotalOverLimit: false,
      isControlCameraLimit: false
    };

    if ([keyControlIncludedEquipmentSelection].includes(name)
      && cameraTypeList.includes(fieldProductType)) {
      const sectionSum = formObjectKeys
        .filter(k => cameraTypeList.includes(opt[k].productType))
        .reduce((acc, k) => acc + parseInt(opt[k].value || 0, 10), 0);
      if (sectionSum > 2) {
        returnObj.controlIncludedCameraTotalOverLimit = true;
        const text = stringInterpolation(
          returnObj.isControlCameraLimit ? maxCameraLimitErrorMessage : errorMessage,
          returnObj.isControlCameraLimit ? secureBusinessMaxCameraLimit : controlIncludedCameraLimit
        );
        setIsAlertOpen(true);
        setBodyText(text);
        return returnObj;
      }
    }

    return returnObj; // Ensure returnObj is always returned
  };

  const getCameraTotal = (fieldProductType, overallCount, easyCount, oneTimeCount) => {
    let count = 0;
    if ([
        keySmartCameraIncludedEquipment,
        keySecurityCameraEquipment,
        keyProtectIncludedEquipmentSelection,
        keyProtectAdditionalEquipment,
        keyControlIncludedEquipmentSelection,
        keyControlAdditionalEquipment
      ].includes(name)
      && typePoeCamera.includes(fieldProductType)) {
      const otherSectionKey = secureBusinessOppositeSectionKeys[name];
      const otherSection = { ...formFields[otherSectionKey] };
      if (overallCount) {
        count = Object.keys(otherSection)
          .filter(ko => {
            return typePoeCamera === ((otherSection[ko] || {}).productType);
          })
          .reduce(
            (acc, ko) => acc + parseInt(otherSection[ko].value || 0, 10),
            0
          );
      }
      if (easyCount) {
        count = Object.keys(otherSection)
          .filter(ko => {
            return typePoeCamera === ((otherSection[ko] || {}).productType) &&
              ko.includes('easy');
          })
          .reduce(
            (acc, ko) => acc + parseInt(otherSection[ko].value || 0, 10),
            0
          );
      }
      if (oneTimeCount) {
        count = Object.keys(otherSection)
          .filter(ko => {
            return typePoeCamera === ((otherSection[ko] || {}).productType) &&
              !ko.includes('easy');
          })
          .reduce(
            (acc, ko) => acc + parseInt(otherSection[ko].value || 0, 10),
            0
          );
      }

    }
    return count;
  };

  // @Todo: check if we need variables oldFields and newFields because they don't exist in Vulcan version
  const calculateEquipmentRates = (oldValues, newValues, fieldName,oldFields,newFields) => {
    let calculationKeys = formObjectKeys;
    if (radioSelection === 'control') {
      oldValues = {...oldFields[keyControlAdditionalEquipment],...oldFields[keyControlIncludedEquipmentSelection]};
      newValues = {...newFields[keyControlAdditionalEquipment],...newFields[keyControlIncludedEquipmentSelection]};
      const pricingFields = [keyControlAdditionalEquipment,keyControlIncludedEquipmentSelection];
      const addedFields = new Set([]);

      if (oldFields) {
        pricingFields.forEach(section => {
          const sectionFields = oldFields[section];
          if (sectionFields) {
            Object.values(sectionFields).forEach(field => {
              if (field.name && !addedFields.has(field.name)) {
                pricingFields.push(field.name);
                addedFields.add(field.name);
              }
            });
          }
        });
      }
      calculationKeys = addedFields;
    }

    const oldSumForSection = calculationKeys.reduce(
      (acc, ko) =>
        acc + parseInt(oldValues[ko].value || 0, 10) * oldValues[ko].price || 0,
      0
    );
    const newSumForSection = calculationKeys.reduce(
      (acc, kn) =>
        acc + parseInt(newValues[kn].value || 0, 10) * newValues[kn].price || 0,
      0
    );

    const newSumForDueMonthly = calculationKeys.reduce(
      (acc, kn) =>
        acc +
        parseInt(newValues[kn].value || 0, 10) * newValues[kn].monthlyPayment || 0,
      0
    );
    const oldSumForDueMonthly = calculationKeys.reduce(
      (acc, kn) =>
        acc +
        parseInt(oldValues[kn].value || 0, 10) * oldValues[kn].monthlyPayment || 0,
      0
    );

    // Calculating Five Year Pricing
    const newSumForDueMonthlyFiveYear = calculationKeys.reduce(
      (acc, kn) =>
        acc +
        parseInt(newValues[kn].value || 0, 10) * newValues[kn].fiveYearEasyPayment || 0,
      0
    );
    const oldSumForDueMonthlyFiveYear = calculationKeys.reduce(
      (acc, kn) =>
        acc +
        parseInt(oldValues[kn].value || 0, 10) * oldValues[kn].fiveYearEasyPayment || 0,
      0
    );

    const currentPrice = equipmentTotalPrice.addAmountToPrice;
    const newPrice = currentPrice + (newSumForSection - oldSumForSection);
    const currentMonthlyPayment = equipmentTotalPrice.addAmountToMonthly || 0;
    let newMonthlyPayment = currentMonthlyPayment + (newSumForDueMonthly - oldSumForDueMonthly);
    // Five Year Payment Calculation
    const currentFiveYearMonthlyPayment = equipmentTotalPrice.addAmountToFiveYearMonthly || 0;
    const newFiveYearMonthlyPayment = currentFiveYearMonthlyPayment + (newSumForDueMonthlyFiveYear - oldSumForDueMonthlyFiveYear);

    if ([
      keyProtectAdditionalEquipment,
      keyControlAdditionalEquipment,
      keyMonitorAdditionalEquipment
    ].includes(name)) {
      // Temperature Sensor Pricing

      // Helper function to get the value, ensuring it's not undefined
      const getValue = (obj, field) => {
        if (obj && obj.hasOwnProperty(field) && obj[field] && obj[field].value) {
          return obj[field].value;
        }
        return 0;
      };

      const oldTempSensor = getValue(oldValues, 'temperatureSensor');
      const oldEasyTempSensor = getValue(oldValues, 'easyTemperatureSensor');
      const oldEasyFiveYearTempSensor = getValue(oldValues, 'easyFiveYearTemperatureSensor');

      const newTempSensor = getValue(newValues, 'temperatureSensor');
      const newEasyTempSensor = getValue(newValues, 'easyTemperatureSensor');
      const newEasyFiveYearTempSensor = getValue(newValues, 'easyFiveYearTemperatureSensor');

      // Handling temperatureSensor field
      if ((fieldName === 'temperatureSensor') && (newEasyTempSensor === 0) && (newEasyFiveYearTempSensor === 0)) {
        // increase
        if (newTempSensor > oldTempSensor) {
          if (newTempSensor === 1 || newTempSensor === 6) {
            newMonthlyPayment += 5;
          }
        }
        // decrease
        else if (newTempSensor < oldTempSensor) {
          if (newTempSensor === 0 || newTempSensor === 5) {
            newMonthlyPayment -= 5;
          }
        }
      }
      // Handling easyTemperatureSensor field
      else if ((fieldName === 'easyTemperatureSensor') && (newTempSensor === 0) && (newEasyFiveYearTempSensor === 0)) {
        // increase
        if (newEasyTempSensor > oldEasyTempSensor) {
          if (newEasyTempSensor === 1 || newEasyTempSensor === 6) {
            newMonthlyPayment += 5;
          }
        }
        // decrease
        else if (newEasyTempSensor < oldEasyTempSensor) {
          if (newEasyTempSensor === 0 || newEasyTempSensor === 5) {
            newMonthlyPayment -= 5;
          }
        }
      }
      // Handling easyFiveYearTemperatureSensor field
      else if ((fieldName === 'easyFiveYearTemperatureSensor') && (newTempSensor === 0) && (newEasyTempSensor === 0)) {
        // increase
        if (newEasyFiveYearTempSensor > oldEasyFiveYearTempSensor) {
          if (newEasyFiveYearTempSensor === 1 || newEasyFiveYearTempSensor === 6) {
            newMonthlyPayment += 5;
          }
        }
        // decrease
        else if (newEasyFiveYearTempSensor < oldEasyFiveYearTempSensor) {
          if (newEasyFiveYearTempSensor === 0 || newEasyFiveYearTempSensor === 5) {
            newMonthlyPayment -= 5;
          }
        }
      }
      // Handling any combination of the three fields
      else if (
        (fieldName === 'easyTemperatureSensor' && (newTempSensor !== 0 || newEasyFiveYearTempSensor !== 0)) ||
        (fieldName === 'temperatureSensor' && (newEasyTempSensor !== 0 || newEasyFiveYearTempSensor !== 0)) ||
        (fieldName === 'easyFiveYearTemperatureSensor' && (newTempSensor !== 0 || newEasyTempSensor !== 0))) {

        if (fieldName === 'temperatureSensor' || fieldName === 'easyTemperatureSensor' || fieldName === 'easyFiveYearTemperatureSensor') {
          const oldTotal = oldTempSensor + oldEasyTempSensor + oldEasyFiveYearTempSensor;
          const newTotal = newTempSensor + newEasyTempSensor + newEasyFiveYearTempSensor;

          // increase
          if (newTotal > oldTotal) {
            if (newTotal === 1 || newTotal === 6) {
              newMonthlyPayment += 5;
            }
          }
          // decrease
          else if (newTotal < oldTotal) {
            if (newTotal === 0 || newTotal === 5) {
              newMonthlyPayment -= 5;
            }
          }
        }
      }
    }

    if (
      [
        keyControlAdditionalEquipment,
        keyProtectAdditionalEquipment,
        keyMonitorAdditionalEquipment
      ].includes(name)
    ) {
      let discountAmount = 0;
      if (newPrice >= 200) {
        discountAmount = 200;
      } else if (newPrice > 0) {
        discountAmount = newPrice;
      }
      if (isHardwareDiscount) setDueNow(dueNow + hardwareDiscountAmount);
      setHardwareDiscountAmount(discountAmount);
    }
    setEquipmentTotalPrice({
      subtractAmountFromPrice: currentPrice || 0,
      addAmountToPrice: newPrice || 0,
      addAmountToMonthly: newMonthlyPayment || 0,
      subtractAmountFromMonthly: currentMonthlyPayment  || 0,
      addAmountToFiveYearMonthly : newFiveYearMonthlyPayment || 0,
      subtractAmountFromFiveYearMonthly : currentFiveYearMonthlyPayment || 0
    });
  };

  // Function to calculate temperature sensors based on key names
  const countTemperatureSensorAll = inputKeys => {
    let tempSensorCount = 0;

    const getValue = (keyPath, sensorKey) => {
      if (
        formFields[keyPath] &&
        formFields[keyPath][sensorKey] &&
        formFields[keyPath][sensorKey].value !== undefined
      ) {
        return formFields[keyPath][sensorKey].value;
      }
      return 0;
    };

    // Ensure inputKeys is an array
    let keys = [];
    if (Array.isArray(inputKeys)) {
      keys = inputKeys;
    } else if (typeof inputKeys === 'string') {
      keys = [inputKeys];
    }

    keys.forEach(key => {
      if (radioSelection === 'monitor') {
        if (key === 'oneTimePayment') {
          tempSensorCount += getValue(
            keyMonitorAdditionalEquipment,
            temperatureSensor
          );
        }
        if (key === 'easy') {
          tempSensorCount += getValue(
            keyMonitorAdditionalEquipment,
            easyTemperatureSensor
          );
        }
        if (key === 'fiveYearEasyPayment') {
          tempSensorCount += getValue(
            keyMonitorAdditionalEquipment,
            easyFiveYearTemperatureSensor
          );
        }
      }

      if (radioSelection === 'protect') {
        if (key === 'oneTimePayment') {
          tempSensorCount += getValue(
            keyProtectAdditionalEquipment,
            temperatureSensor
          );
        }
        if (key === 'easy') {
          tempSensorCount += getValue(
            keyProtectAdditionalEquipment,
            easyTemperatureSensor
          );
        }
        if (key === 'fiveYearEasyPayment') {
          tempSensorCount += getValue(
            keyProtectAdditionalEquipment,
            easyFiveYearTemperatureSensor
          );
        }
      }

      if (radioSelection === 'control') {
        if (key === 'oneTimePayment') {
          tempSensorCount += getValue(
            keyControlAdditionalEquipment,
            temperatureSensor
          );
        }
        if (key === 'easy') {
          tempSensorCount += getValue(
            keyControlAdditionalEquipment,
            easyTemperatureSensor
          );
        }
        if (key === 'fiveYearEasyPayment') {
          tempSensorCount += getValue(
            keyControlAdditionalEquipment,
            easyFiveYearTemperatureSensor
          );
        }
      }
    });
    return tempSensorCount;
  };

  const [temperatureSensorCountAll, setTemperatureSensorCountAll] = useState(0);

  useEffect(() => {
    setTemperatureSensorCountAll(
      countTemperatureSensorAll([
        'oneTimePayment',
        'easy',
        'fiveYearEasyPayment'
      ])
    );
  }, [formFields]);

  useEffect(() => {
    // Logic to update form fields based on the temperatureSensorCountAll
    const fields = { ...formFields };
    if (fields && (fields[keyMonitorAdditionalEquipment] || fields[keyControlAdditionalEquipment] || fields[keyProtectAdditionalEquipment])) {
      if (temperatureSensorCountAll < 1 || temperatureSensorCountAll === undefined) {
        if (radioSelection === 'monitor') {
          setTempSensorPrice({
            temperatureMonitoringFiveDollar:0,
            temperatureMonitoringTenDollar:0
          });
          fields[
            keyMonitorAdditionalEquipment
            ].temperatureMonitoringFiveDollar.isVisible = false;
          fields[
            keyMonitorAdditionalEquipment
            ].temperatureMonitoringTenDollar.isVisible = false;
          fields[
            keyMonitorAdditionalEquipment
            ].temperatureMonitoringFiveDollar.value = 0;
          fields[
            keyMonitorAdditionalEquipment
            ].temperatureMonitoringTenDollar.value = 0;
        }
        if (radioSelection === 'protect') {
          setTempSensorPrice({
            temperatureMonitoringFiveDollar:0,
            temperatureMonitoringTenDollar:0
          });
          fields[
            keyProtectAdditionalEquipment
            ].temperatureMonitoringFiveDollar.isVisible = false;

          fields[
            keyProtectAdditionalEquipment
            ].temperatureMonitoringTenDollar.isVisible = false;
          fields[
            keyProtectAdditionalEquipment
            ].temperatureMonitoringFiveDollar.value = 0;
          fields[
            keyProtectAdditionalEquipment
            ].temperatureMonitoringTenDollar.value = 0;
        }
        if (radioSelection === 'control') {
          setTempSensorPrice({
            temperatureMonitoringFiveDollar:0,
            temperatureMonitoringTenDollar:0
          });
          fields[
            keyControlAdditionalEquipment
            ].temperatureMonitoringFiveDollar.isVisible = false;
          fields[
            keyControlAdditionalEquipment
            ].temperatureMonitoringTenDollar.isVisible = false;
          fields[
            keyControlAdditionalEquipment
            ].temperatureMonitoringFiveDollar.value = 0;
          fields[
            keyControlAdditionalEquipment
            ].temperatureMonitoringTenDollar.value = 0;
        }
      } else if (temperatureSensorCountAll < 6) {
        if (radioSelection === 'monitor') {
          setTempSensorPrice({
            temperatureMonitoringFiveDollar:5,
            temperatureMonitoringTenDollar:0
          });
          fields[
            keyMonitorAdditionalEquipment
            ].temperatureMonitoringFiveDollar.isVisible = true;
          fields[
            keyMonitorAdditionalEquipment
            ].temperatureMonitoringTenDollar.isVisible = false;
          fields[
            keyMonitorAdditionalEquipment
            ].temperatureMonitoringFiveDollar.value = 1;
          fields[
            keyMonitorAdditionalEquipment
            ].temperatureMonitoringTenDollar.value = 0;
        }
        if (radioSelection === 'protect') {
          setTempSensorPrice({
            temperatureMonitoringFiveDollar:5,
            temperatureMonitoringTenDollar:0
          });
          fields[
            keyProtectAdditionalEquipment
            ].temperatureMonitoringFiveDollar.isVisible = true;

          fields[
            keyProtectAdditionalEquipment
            ].temperatureMonitoringTenDollar.isVisible = false;
          fields[
            keyProtectAdditionalEquipment
            ].temperatureMonitoringFiveDollar.value = 1;
          fields[
            keyProtectAdditionalEquipment
            ].temperatureMonitoringTenDollar.value = 0;
        }
        if (radioSelection === 'control') {
          setTempSensorPrice({
            temperatureMonitoringFiveDollar:5,
            temperatureMonitoringTenDollar:0
          });
          fields[
            keyControlAdditionalEquipment
            ].temperatureMonitoringFiveDollar.isVisible = true;
          fields[
            keyControlAdditionalEquipment
            ].temperatureMonitoringTenDollar.isVisible = false;
          fields[
            keyControlAdditionalEquipment
            ].temperatureMonitoringFiveDollar.value = 1;
          fields[
            keyControlAdditionalEquipment
            ].temperatureMonitoringTenDollar.value = 0;
        }
      } else if (temperatureSensorCountAll > 5) {
        if (radioSelection === 'monitor') {
          setTempSensorPrice({
            temperatureMonitoringFiveDollar:5,
            temperatureMonitoringTenDollar:5
          });
          fields[
            keyMonitorAdditionalEquipment
            ].temperatureMonitoringFiveDollar.isVisible = false;
          fields[
            keyMonitorAdditionalEquipment
            ].temperatureMonitoringTenDollar.isVisible = true;
          fields[
            keyMonitorAdditionalEquipment
            ].temperatureMonitoringFiveDollar.value = 0;
          fields[
            keyMonitorAdditionalEquipment
            ].temperatureMonitoringTenDollar.value = 1;
        }
        if (radioSelection === 'protect') {
          setTempSensorPrice({
            temperatureMonitoringFiveDollar:5,
            temperatureMonitoringTenDollar:5
          });
          fields[
            keyProtectAdditionalEquipment
            ].temperatureMonitoringFiveDollar.isVisible = false;

          fields[
            keyProtectAdditionalEquipment
            ].temperatureMonitoringTenDollar.isVisible = true;
          fields[
            keyProtectAdditionalEquipment
            ].temperatureMonitoringFiveDollar.value = 0;
          fields[
            keyProtectAdditionalEquipment
            ].temperatureMonitoringTenDollar.value = 1;
        }
        if (radioSelection === 'control') {
          setTempSensorPrice({
            temperatureMonitoringFiveDollar:5,
            temperatureMonitoringTenDollar:5
          });
          fields[
            keyControlAdditionalEquipment
            ].temperatureMonitoringFiveDollar.isVisible = false;
          fields[
            keyControlAdditionalEquipment
            ].temperatureMonitoringTenDollar.isVisible = true;
          fields[
            keyControlAdditionalEquipment
            ].temperatureMonitoringFiveDollar.value = 0;
          fields[
            keyControlAdditionalEquipment
            ].temperatureMonitoringTenDollar.value = 1;
        }
      }
    }
    setRenderTempSensor(!renderTempSensor);
  }, [temperatureSensorCountAll]);

  const handleFormField = ({ name: fieldName, value }) => {
    // below fields will be inactive, can't add/remove manually
    if ([
      controlIncludedSdCard,
      microSdCard128Gb,
      easyMicroSdCard128Gb,
      easyFiveYearMicroSdCard128Gb,
      temperatureProbe,
      temperatureProbe,
      easyTemperatureProbe,
      easyFiveYearTemperatureProbe,
      controlIncludedPaid2TbSvr
    ].includes(fieldName)) {
      return null;
    }

    const fields = _.cloneDeep(formFields);
    const oldFields = _.cloneDeep(formFields);
    const oldValues = _.cloneDeep(formFields[name]);

    const checkAllPoeCameraValues = () => {
      let result;

      switch (radioSelection) {
        case 'control':
          result = getObjectValue(fields, `${keyControlIncludedEquipmentSelection}.poeTurretCameraFree`) > 0 ||
            getObjectValue(fields, `${keyControlIncludedEquipmentSelection}.poeDomeCameraFree`) > 0 ||
            getObjectValue(fields, `${keyControlAdditionalEquipment}.poeTurretCamera`) > 0 ||
            getObjectValue(fields, `${keyControlAdditionalEquipment}.poeDomeCamera`) > 0 ||
            getObjectValue(fields, `${keyControlAdditionalEquipment}.easyPoeTurret`) > 0 ||
            getObjectValue(fields, `${keyControlAdditionalEquipment}.easyPoeDomeCamera`) > 0 ||
            getObjectValue(fields, `${keyControlAdditionalEquipment}.easyFiveYearPoeTurretCamera`) > 0 ||
            getObjectValue(fields, `${keyControlAdditionalEquipment}.easyFiveYearPoeDomeCamera`) > 0;
          break;
        case 'protect':
          result = getObjectValue(fields, `${keyProtectIncludedEquipmentSelection}.poeTurretCameraFree`) > 0 ||
            getObjectValue(fields, `${keyProtectIncludedEquipmentSelection}.poeDomeCameraFree`) > 0 ||
            getObjectValue(fields, `${keyProtectAdditionalEquipment}.poeTurretCamera`) > 0 ||
            getObjectValue(fields, `${keyProtectAdditionalEquipment}.poeDomeCamera`) > 0 ||
            getObjectValue(fields, `${keyProtectAdditionalEquipment}.easyPoeTurret`) > 0 ||
            getObjectValue(fields, `${keyProtectAdditionalEquipment}.easyPoeDomeCamera`) > 0 ||
            getObjectValue(fields, `${keyProtectAdditionalEquipment}.easyFiveYearPoeTurretCamera`) > 0 ||
            getObjectValue(fields, `${keyProtectAdditionalEquipment}.easyFiveYearPoeDomeCamera`) > 0;
          break;
        default:
          result = false;
      }

      return result;
    };

    let poeCameraCountAll = 0;

    fields[name][fieldName] = {
      ...fields[name][fieldName],
      value: parseInt(value, 10)
    };

    const countAllPoeCamera = () => {
      let result;

      switch (radioSelection) {
        case 'control':
          result =
            getObjectValue(fields, `${keyControlIncludedEquipmentSelection}.${poeTurretCameraFree}`) +
            getObjectValue(fields, `${keyControlAdditionalEquipment}.${poeTurretCamera}`) +
            getObjectValue(fields, `${keyControlAdditionalEquipment}.${easyPoeTurret}`) +
            getObjectValue(fields, `${keyControlAdditionalEquipment}.${easyFiveYearPoeTurretCamera}`);
          break;
        default:
          result = 0;
      }

      return result;
    };
    poeCameraCountAll = countAllPoeCamera();

    const setSdCard = (obj) => {
      const controlIncludedPaid2TbSvrValue = getObjectValue(obj, `${keyControlIncludedEquipmentSelection}.controlIncludedPaid2TbSvr`);
      if (radioSelection === 'control') {
        const cameraSum =
          parseInt(getObjectValue(obj, `${keyControlIncludedEquipmentSelection}.indoorCameraFree`), 10) +
          parseInt(getObjectValue(obj, `${keyControlIncludedEquipmentSelection}.outdoorCameraFree`), 10);
        if (cameraSum > 0 && !(controlIncludedPaid2TbSvrValue >= 1)) {
          setObjectValue(obj, `${keyControlIncludedEquipmentSelection}.controlIncludedSdCard`, cameraSum);
        } else if (cameraSum === 0 || controlIncludedPaid2TbSvrValue >= 1) {
          setObjectValue(obj, `${keyControlIncludedEquipmentSelection}.controlIncludedSdCard`, 0);
        }
      }
    };

    const fieldProductType = fields[name][fieldName].productType;
    const { controlIncludedCameraTotalOverLimit} = getControlIncludedCameraTotal(fields[name], fieldProductType);
    const { totalOverLimit} = checkIfTotalOverLimit(fields[name], fieldProductType);

    if (!totalOverLimit && !controlIncludedCameraTotalOverLimit) {
      if (!isFormTouched) {
        setIsFormTouched(true);
      }
      if (checkIfHasSwitchOption() && !fieldName.startsWith('easy')) {
        const poeCameraCount = formObjectKeys
          .filter(
            kc =>
              fields[name][kc].productType === typePoeCamera &&
              !kc.startsWith('easy')
          )
          .reduce(
            (acc, kc) => acc + parseInt(fields[name][kc].value || 0, 10),
            0
          );
        const switchKey = formObjectKeys.filter(k => (fields[name][k].productType === typeSwitch && fields[name][k].name === poeSwitchZero));
        if (switchKey.length > 0) {
          const total = poeCameraCount + getCameraTotal(fieldProductType, true, undefined, undefined);
          for (let i = 0; i < switchKey.length; i += 1) {
            fields[name][switchKey[i]].isVisible = poeCameraCount > 0 && total < 5;
            fields[name][switchKey[i]].value = poeCameraCount > 0 && total < 5 ? 1 : 0;
          }
          fields[keyControlIncludedEquipmentSelection][controlIncludedPaid2TbSvr].value = poeCameraCountAll > 0 ? 1 : 0;
          setSdCard(fields);
        }
        const includedSwitchKey = formObjectKeys.filter(k => {
          return (fields[name][k].productType === typeSwitch && fields[name][k].name === poeSwitchIncludedEquipment);
        });
        if ([poeTurretCameraFree, poeDomeCameraFree].includes(fieldName)) {
          if (radioSelection === 'protect') {
            fields[keyProtectIncludedEquipmentSelection].poeSwitchIncludedEquipment.isVisible = value > 0;
            fields[keyProtectIncludedEquipmentSelection].poeSwitchIncludedEquipment.value = value > 0 ? 1 : 0;
          }
          if (radioSelection === 'control') {
            fields[keyControlIncludedEquipmentSelection].poeSwitchIncludedEquipment.isVisible = value > 0;
            fields[keyControlIncludedEquipmentSelection].poeSwitchIncludedEquipment.value = value > 0 ? 1 : 0; // this code is showing poeswitch in included eqp
          }
        }
        // Temperature Probe Visiblity
        const additionalEquipmentTempSensorArray = [temperatureSensor, easyTemperatureSensor,easyFiveYearTemperatureSensor];
        if (additionalEquipmentTempSensorArray.includes(fieldName) && value > 0) {
          if (radioSelection === 'protect') {
            fields[keyProtectAdditionalEquipment].temperatureProbe.isVisible = true;
            fields[keyProtectAdditionalEquipment].temperatureProbe.value = value;
          }
          if (radioSelection === 'control') {
            fields[keyControlAdditionalEquipment].temperatureProbe.isVisible = true;
            fields[keyControlAdditionalEquipment].temperatureProbe.value = value;
          }
          if (radioSelection === 'monitor') {
            fields[keyMonitorAdditionalEquipment].temperatureProbe.isVisible = true;
            fields[keyMonitorAdditionalEquipment].temperatureProbe.value = value;
          }
        }
        if (additionalEquipmentTempSensorArray.includes(fieldName) && value < 1) {
          if (radioSelection === 'protect') {
            fields[keyProtectAdditionalEquipment].temperatureProbe.isVisible = false;
            fields[keyProtectAdditionalEquipment].temperatureProbe.value = 0;
          }
          if (radioSelection === 'control') {
            fields[keyControlAdditionalEquipment].temperatureProbe.isVisible = false;
            fields[keyControlAdditionalEquipment].temperatureProbe.value = 0;
          }
          if (radioSelection === 'monitor') {
            fields[keyMonitorAdditionalEquipment].temperatureProbe.isVisible = false;
            fields[keyMonitorAdditionalEquipment].temperatureProbe.value = 0;
          }
        }
        if (includedSwitchKey.length > 0) {
          const hidePoeIncludedCamera = checkAllPoeCameraValues();
          if (!hidePoeIncludedCamera) {
            for (let i = 0; i < includedSwitchKey.length; i += 1) {
              fields[name][includedSwitchKey[i]].isVisible = poeCameraCount > 0;
              fields[name][includedSwitchKey[i]].value = poeCameraCount > 0 ? 1 : 0;
            }
          }
          if (poeCameraCount + getCameraTotal(fieldProductType, undefined, true, undefined) >= 5) {
            fields[secureBusinessOppositeSectionKeys[name]][fieldEasyPoeSwitchNonZero].isVisible = true;
            fields[secureBusinessOppositeSectionKeys[name]][fieldEasyPoeSwitchNonZero].value = 1;
          }
          if (poeCameraCount + getCameraTotal(fieldProductType, undefined, undefined, true) >= 5) {
            fields[secureBusinessOppositeSectionKeys[name]][fieldPoeSwitchNonZero].isVisible = true;
            fields[secureBusinessOppositeSectionKeys[name]][fieldPoeSwitchNonZero].value = 1;
          }
          if (poeCameraCount + getCameraTotal(fieldProductType, undefined, true, undefined) < 5) {
            fields[secureBusinessOppositeSectionKeys[name]][fieldEasyPoeSwitchNonZero].isVisible = undefined;
            fields[secureBusinessOppositeSectionKeys[name]][fieldEasyPoeSwitchNonZero].value = 0;
          }
          if (poeCameraCount + getCameraTotal(fieldProductType, undefined, undefined, true) < 5) {
            fields[secureBusinessOppositeSectionKeys[name]][fieldPoeSwitchNonZero].isVisible = undefined;
            fields[secureBusinessOppositeSectionKeys[name]][fieldPoeSwitchNonZero].value = 0;
          }
        }
        const switchKeyNonZero = formObjectKeys.filter(k => {
          return (fields[name][k].productType === typeSwitch &&
            fields[name][k].name === poeSwitchNonZero);
        });
        if (switchKeyNonZero.length > 0) {
          const total = poeCameraCount + getCameraTotal(fieldProductType, true, undefined, undefined);
          for (let i = 0; i < switchKeyNonZero.length; i += 1) {
            fields[name][switchKeyNonZero[i]].isVisible = total > 4;
            fields[name][switchKeyNonZero[i]].value = total > 4 ? 1 : 0;
          }
        }
        const hidePoeIncludedCamera = checkAllPoeCameraValues();
        if (!hidePoeIncludedCamera) {
          if (radioSelection === 'protect') {
            fields[keyProtectIncludedEquipmentSelection].poeSwitchIncludedEquipment.isVisible = false;
            fields[keyProtectIncludedEquipmentSelection].poeSwitchIncludedEquipment.value = 0;
          }
          if (radioSelection === 'control') {
            fields[keyControlIncludedEquipmentSelection].poeSwitchIncludedEquipment.isVisible = false;
            fields[keyControlIncludedEquipmentSelection].poeSwitchIncludedEquipment.value = 0;
          }
        }

        // 2TB SVR addition with Poe Turret Camera
        if ([poeTurretCameraFree].includes(fieldName)) {
          if (radioSelection === 'control') {
            if (value > 0) {
              setObjectValue(fields, `${keyControlIncludedEquipmentSelection}.controlIncludedPaid2TbSvr`, 1);
              setObjectValue(fields, `${keyControlIncludedEquipmentSelection}.controlIncludedPaid2TbSvr`, true, 'isVisible');
            } else if (poeCameraCountAll === 0) {
              setObjectValue(fields, `${keyControlIncludedEquipmentSelection}.controlIncludedPaid2TbSvr`, 0);
            }
          }
        }
        // Adding SD Card equal qty to indoor/outdoor camera
        // if turret camera is added then sd cards are not added,
        // if no turret camera then sd cards are added with indoor/outdoor control camera
        if ([
          indoorCameraFree,
          outdoorCameraFree,
          poeTurretCameraFree
        ].includes(fieldName)) {
          setSdCard(fields);
        }

        // add  sd card to additional control equipment
        if ([
          indoorCamera,
          outdoorCamera,
          poeTurretCameraFree
        ].includes(fieldName)) {
          const controlIncludedPaid2TbSvrValue = getObjectValue(fields, `${keyControlIncludedEquipmentSelection}.controlIncludedPaid2TbSvr`);
          if (radioSelection === 'control') {
            const cameraSum =
              parseInt(getObjectValue(fields, `${keyControlAdditionalEquipment}.indoorCamera`), 10) +
              parseInt(getObjectValue(fields, `${keyControlAdditionalEquipment}.outdoorCamera`), 10);
            if (cameraSum > 0 && !(controlIncludedPaid2TbSvrValue >= 1)) {
              setObjectValue(fields, `${keyControlAdditionalEquipment}.microSdCard128Gb`, cameraSum);
            } else if (cameraSum === 0 || controlIncludedPaid2TbSvrValue >= 1) {
              setObjectValue(fields, `${keyControlAdditionalEquipment}.microSdCard128Gb`, 0);
            }
          }
        }

        // Resetting Five Year fields as per turretCamera Selection
        if ([
          easyFiveYearIndoorCamera,
          easyFiveYearOutdoorCamera,
          poeTurretCameraFree
        ].includes(fieldName)) {
          const controlIncludedPaid2TbSvrValue = getObjectValue(fields, `${keyControlIncludedEquipmentSelection}.controlIncludedPaid2TbSvr`);
          if (radioSelection === 'control') {
            const cameraSum =
              parseInt(getObjectValue(fields, `${keyControlAdditionalEquipment}.easyFiveYearIndoorCamera`), 10) +
              parseInt(getObjectValue(fields, `${keyControlAdditionalEquipment}.easyFiveYearOutdoorCamera`), 10);
            if (cameraSum > 0 && !(controlIncludedPaid2TbSvrValue >= 1)) {
              setObjectValue(fields, `${keyControlAdditionalEquipment}.easyFiveYearMicroSdCard128Gb`, cameraSum);
            } else if (cameraSum === 0 || controlIncludedPaid2TbSvrValue >= 1) {
              setObjectValue(fields, `${keyControlAdditionalEquipment}.easyFiveYearMicroSdCard128Gb`, 0);
            }
          }
        }

        // Resetting Three Year fields as per turretCamera Selection
        if ([
          easyIndoorCamera,
          easyOutdoorCamera,
          poeTurretCameraFree
        ].includes(fieldName)) {
          const controlIncludedPaid2TbSvrValue = getObjectValue(fields, `${keyControlIncludedEquipmentSelection}.controlIncludedPaid2TbSvr`);
          if (radioSelection === 'control') {
            const cameraSum =
              parseInt(getObjectValue(fields, `${keyControlAdditionalEquipment}.easyIndoorCamera`), 10) +
              parseInt(getObjectValue(fields, `${keyControlAdditionalEquipment}.easyOutdoorCamera`), 10);
            if (cameraSum > 0 && !(controlIncludedPaid2TbSvrValue >= 1)) {
              setObjectValue(fields, `${keyControlAdditionalEquipment}.easyMicroSdCard128Gb`, cameraSum);
            } else if (cameraSum === 0 || controlIncludedPaid2TbSvrValue >= 1) {
              setObjectValue(fields, `${keyControlAdditionalEquipment}.easyMicroSdCard128Gb`, 0);
            }
          }
        }
      } else if (checkIfHasSwitchOption() && fieldName.startsWith('easyFiveYear')) {
        const poeCameraCount = formObjectKeys
          .filter(kc => (fields[name][kc].productType === typePoeCamera && kc.startsWith('easyFiveYear')))
          .reduce((acc, kc) => acc + parseInt(fields[name][kc].value || 0, 10), 0);
        const switchKey = formObjectKeys.filter(k => (fields[name][k].productType === typeSwitch && fields[name][k].name === fieldEasyFiveYearFivePortPoeSwitch));
        if ([poeTurretCameraFree, poeDomeCameraFree].includes(fieldName)) {
          if (radioSelection === 'protect') {
            fields[keyProtectIncludedEquipmentSelection].poeSwitchIncludedEquipment.isVisible = value > 0;
            fields[keyProtectIncludedEquipmentSelection].poeSwitchIncludedEquipment.value = value > 0 ? 1 : 0;
          }
          if (radioSelection === 'control') {
            fields[keyControlIncludedEquipmentSelection].poeSwitchIncludedEquipment.isVisible = value > 0;
            fields[keyControlIncludedEquipmentSelection].poeSwitchIncludedEquipment.value = value > 0 ? 1 : 0;
          }
        }
        // Easy Temperature Probe Visible
        const additionalEquipmentTempSensorArray = [temperatureSensor, easyTemperatureSensor,easyFiveYearTemperatureSensor];
        if (additionalEquipmentTempSensorArray.includes(fieldName) && value > 0) {
          if (radioSelection === 'protect') {
            fields[keyProtectAdditionalEquipment].easyFiveYearTemperatureProbe.isVisible = true;
            fields[keyProtectAdditionalEquipment].easyFiveYearTemperatureProbe.value = value;
          }
          if (radioSelection === 'control') {
            fields[keyControlAdditionalEquipment].easyFiveYearTemperatureProbe.isVisible = true;
            fields[keyControlAdditionalEquipment].easyFiveYearTemperatureProbe.value = value;
          }
          if (radioSelection === 'monitor') {
            fields[keyMonitorAdditionalEquipment].easyFiveYearTemperatureProbe.isVisible = true;
            fields[keyMonitorAdditionalEquipment].easyFiveYearTemperatureProbe.value = value;
          }
        }
        if ((additionalEquipmentTempSensorArray.includes(fieldName)) && value < 1) {
          if (radioSelection === 'protect') {
            fields[keyProtectAdditionalEquipment].easyFiveYearTemperatureProbe.isVisible = false;
            fields[keyProtectAdditionalEquipment].easyFiveYearTemperatureProbe.value = 0;
          }
          if (radioSelection === 'control') {
            fields[keyControlAdditionalEquipment].easyFiveYearTemperatureProbe.isVisible = false;
            fields[keyControlAdditionalEquipment].easyFiveYearTemperatureProbe.value = 0;
          }
          if (radioSelection === 'monitor') {
            fields[keyMonitorAdditionalEquipment].easyFiveYearTemperatureProbe.isVisible = false;
            fields[keyMonitorAdditionalEquipment].easyFiveYearTemperatureProbe.value = 0;
          }
        }
        if (switchKey.length > 0) {
          const total = poeCameraCount + getCameraTotal(fieldProductType, true, undefined, undefined);
          for (let i = 0; i < switchKey.length; i += 1) {
            fields[name][switchKey[i]].isVisible = poeCameraCount > 0 && total < 5;
            fields[name][switchKey[i]].value = poeCameraCount > 0 && total < 5 ? 1 : 0;
          }
          fields[keyControlIncludedEquipmentSelection][controlIncludedPaid2TbSvr].value = poeCameraCountAll > 0 ? 1 : 0;
          setSdCard(fields);
        }
        const switchKeyNonZero = formObjectKeys.filter(k =>
          (fields[name][k].productType === typeSwitch &&
            fields[name][k].name === fieldEasyFiveYearFivePortPoeSwitchPaid)
        );
        if (switchKeyNonZero.length > 0) {
          const total = poeCameraCount + getCameraTotal(fieldProductType, true, undefined, undefined);
          for (let i = 0; i < switchKeyNonZero.length; i += 1) {
            fields[name][switchKeyNonZero[i]].isVisible = total > 4;
            fields[name][switchKeyNonZero[i]].value = total > 4 ? 1 : 0;
          }
        }
        const hidePoeIncludedCamera = checkAllPoeCameraValues();
        if (!hidePoeIncludedCamera) {
          if (radioSelection === 'protect') {
            fields[keyProtectIncludedEquipmentSelection].poeSwitchIncludedEquipment.isVisible = false;
            fields[keyProtectIncludedEquipmentSelection].poeSwitchIncludedEquipment.value = 0;
          }
          if (radioSelection === 'control') {
            fields[keyControlIncludedEquipmentSelection].poeSwitchIncludedEquipment.isVisible = false;
            fields[keyControlIncludedEquipmentSelection].poeSwitchIncludedEquipment.value = 0;
          }
        }

        if ([
          easyFiveYearIndoorCamera,
          easyFiveYearOutdoorCamera,
          poeTurretCameraFree
        ].includes(fieldName)) {
          const controlIncludedPaid2TbSvrValue = getObjectValue(fields, `${keyControlIncludedEquipmentSelection}.controlIncludedPaid2TbSvr`);
          if (radioSelection === 'control') {
            const cameraSum =
              parseInt(getObjectValue(fields, `${keyControlAdditionalEquipment}.easyFiveYearIndoorCamera`), 10) +
              parseInt(getObjectValue(fields, `${keyControlAdditionalEquipment}.easyFiveYearOutdoorCamera`), 10);
            if (cameraSum > 0 && !(controlIncludedPaid2TbSvrValue >= 1)) {
              setObjectValue(fields, `${keyControlAdditionalEquipment}.easyFiveYearMicroSdCard128Gb`, cameraSum);
            } else if (cameraSum === 0 || controlIncludedPaid2TbSvrValue >= 1) {
              setObjectValue(fields, `${keyControlAdditionalEquipment}.easyFiveYearMicroSdCard128Gb`, 0);
            }
          }
        }
      } else if (checkIfHasSwitchOption() && fieldName.startsWith('easy')) {
        const poeCameraCount = formObjectKeys
          .filter(
            kc =>
              fields[name][kc].productType === typePoeCamera &&
              kc.startsWith('easy')
          )
          .reduce(
            (acc, kc) => acc + parseInt(fields[name][kc].value || 0, 10),
            0
          );
        const switchKey = formObjectKeys.filter(
          k =>
            (fields[name][k].productType === typeSwitch &&
              fields[name][k].name === easyPoeSwitchZero)
        );
        if ([poeTurretCameraFree, poeDomeCameraFree].includes(fieldName)) {
          if (radioSelection === 'protect') {
            fields[keyProtectIncludedEquipmentSelection].poeSwitchIncludedEquipment.isVisible = value > 0;
            fields[keyProtectIncludedEquipmentSelection].poeSwitchIncludedEquipment.value = value > 0 ? 1 : 0;
          }
          if (radioSelection === 'control') {
            fields[keyControlIncludedEquipmentSelection].poeSwitchIncludedEquipment.isVisible = value > 0;
            fields[keyControlIncludedEquipmentSelection].poeSwitchIncludedEquipment.value = value > 0 ? 1 : 0;
          }
        }
        // Easy Temperature Probe Visible
        const additionalEquipmentTempSensorArray = [temperatureSensor, easyTemperatureSensor];
        if (additionalEquipmentTempSensorArray.includes(fieldName) && value > 0) {
          if (radioSelection === 'protect') {
            fields[keyProtectAdditionalEquipment].easyTemperatureProbe.isVisible = true;
            fields[keyProtectAdditionalEquipment].easyTemperatureProbe.value = value;
          }
          if (radioSelection === 'control') {
            fields[keyControlAdditionalEquipment].easyTemperatureProbe.isVisible = true;
            fields[keyControlAdditionalEquipment].easyTemperatureProbe.value = value;
          }
          if (radioSelection === 'monitor') {
            fields[keyMonitorAdditionalEquipment].easyTemperatureProbe.isVisible = true;
            fields[keyMonitorAdditionalEquipment].easyTemperatureProbe.value = value;
          }
        }
        if ((additionalEquipmentTempSensorArray.includes(fieldName)) && value < 1) {
          if (radioSelection === 'protect') {
            fields[keyProtectAdditionalEquipment].easyTemperatureProbe.isVisible = false;
            fields[keyProtectAdditionalEquipment].easyTemperatureProbe.value = 0;
          }
          if (radioSelection === 'control') {
            fields[keyControlAdditionalEquipment].easyTemperatureProbe.isVisible = false;
            fields[keyControlAdditionalEquipment].easyTemperatureProbe.value = 0;
          }
          if (radioSelection === 'monitor') {
            fields[keyMonitorAdditionalEquipment].easyTemperatureProbe.isVisible = false;
            fields[keyMonitorAdditionalEquipment].easyTemperatureProbe.value = 0;
          }
        }
        if (switchKey.length > 0) {
          const total = poeCameraCount + getCameraTotal(fieldProductType, true, undefined, undefined);
          for (let i = 0; i < switchKey.length; i += 1) {
            fields[name][switchKey[i]].isVisible = poeCameraCount > 0 && total < 5;
            fields[name][switchKey[i]].value = poeCameraCount > 0 && total < 5 ? 1 : 0;
          }
          fields[keyControlIncludedEquipmentSelection][controlIncludedPaid2TbSvr].value = poeCameraCountAll > 0 ? 1 : 0;
          setSdCard(fields);
        }

        const switchKeyNonZero = formObjectKeys.filter(k => (fields[name][k].productType === typeSwitch && fields[name][k].name === easyPoeSwitchNonZero));
        if (switchKeyNonZero.length > 0) {
          const total = poeCameraCount + getCameraTotal(fieldProductType, true, undefined, undefined);
          for (let i = 0; i < switchKeyNonZero.length; i += 1) {
            setObjectValue(fields, `${name}.${switchKeyNonZero[i]}`, total > 4, 'isVisible');
            setObjectValue(fields, `${name}.${switchKeyNonZero[i]}`, total > 4 ? 1 : 0);
          }
        }

        const hidePoeIncludedCamera = checkAllPoeCameraValues();
        if (!hidePoeIncludedCamera) {
          // @Todo: need to change the radioSelection variable logic
          if (radioSelection === 'smartca') {
            setObjectValue(fields, `${keySmartCameraIncludedEquipment}.poeSwitchIncludedEquipment`, false, 'isVisible');
            setObjectValue(fields, `${keySmartCameraIncludedEquipment}.poeSwitchIncludedEquipment`, 0);
          }
          if (radioSelection === 'protect') {
            setObjectValue(fields, `${keyProtectIncludedEquipmentSelection}.poeSwitchIncludedEquipment`, false, 'isVisible');
            setObjectValue(fields, `${keyProtectIncludedEquipmentSelection}.poeSwitchIncludedEquipment`, 0);
          }
          if (radioSelection === 'control') {
            setObjectValue(fields, `${keyControlIncludedEquipmentSelection}.poeSwitchIncludedEquipment`, false, 'isVisible');
            setObjectValue(fields, `${keyControlIncludedEquipmentSelection}.poeSwitchIncludedEquipment`, 0);
          }
        }

        // Setting Three Year fields as per turretCamera Selection
        if ([
          easyIndoorCamera,
          easyOutdoorCamera,
          poeTurretCameraFree
        ].includes(fieldName)) {
          const controlIncludedPaid2TbSvrValue = getObjectValue(fields, `${keyControlIncludedEquipmentSelection}.controlIncludedPaid2TbSvr`);
          if (radioSelection === 'control') {
            const cameraSum =
              parseInt(getObjectValue(fields, `${keyControlAdditionalEquipment}.easyIndoorCamera`), 10) +
              parseInt(getObjectValue(fields, `${keyControlAdditionalEquipment}.easyOutdoorCamera`), 10);
            if (cameraSum > 0 && !(controlIncludedPaid2TbSvrValue >= 1)) {
              setObjectValue(fields, `${keyControlAdditionalEquipment}.easyMicroSdCard128Gb`, cameraSum);
            } else if (cameraSum === 0 || controlIncludedPaid2TbSvrValue >= 1) {
              setObjectValue(fields, `${keyControlAdditionalEquipment}.easyMicroSdCard128Gb`, 0);
            }
          }
        }
      }
      if ([
        keyControlIncludedEquipmentSelection,
        keyControlAdditionalEquipment,
        keyProtectAdditionalEquipment,
        keyMonitorAdditionalEquipment,
        keySecurityCameraEquipment
      ].includes(name)) {
        calculateEquipmentRates(oldValues, fields[name], fieldName,oldFields,fields);
      }
      setFormFields(fields);
    }
  };

  const countPoeCameras = (key) => {
    let poeCameraCount = 0;

    if (name === keySecurityCameraEquipment) {
      if (key === 'onetimePayment') {
        poeCameraCount =
          getObjectValue(formFields, `${keySmartCameraIncludedEquipment}.${poeTurretCameraFree}`) +
          getObjectValue(formFields, `${keySmartCameraIncludedEquipment}.${poeDomeCameraFree}`) +
          getObjectValue(formFields, `${keySecurityCameraEquipment}.${poeTurretCamera}`) +
          getObjectValue(formFields, `${keySecurityCameraEquipment}.${poeDomeCamera}`);
      }
      if (key === 'easyPayment') {
        poeCameraCount =
          getObjectValue(formFields, `${keySmartCameraIncludedEquipment}.${poeTurretCameraFree}`) +
          getObjectValue(formFields, `${keySmartCameraIncludedEquipment}.${poeDomeCameraFree}`) +
          getObjectValue(formFields, `${keySecurityCameraEquipment}.${easyPoeTurret}`) +
          getObjectValue(formFields, `${keySecurityCameraEquipment}.${easyPoeDomeCamera}`);
      }
    } else if (radioSelection === 'protect') {
      if (key === 'onetimePayment') {
        poeCameraCount =
          getObjectValue(formFields, `${keyProtectIncludedEquipmentSelection}.${poeTurretCameraFree}`) +
          getObjectValue(formFields, `${keyProtectIncludedEquipmentSelection}.${poeDomeCameraFree}`) +
          getObjectValue(formFields, `${keyProtectAdditionalEquipment}.${poeTurretCamera}`) +
          getObjectValue(formFields, `${keyProtectAdditionalEquipment}.${poeDomeCamera}`);
      }
      if (key === 'easyPayment') {
        poeCameraCount =
          getObjectValue(formFields, `${keyProtectIncludedEquipmentSelection}.${poeTurretCameraFree}`) +
          getObjectValue(formFields, `${keyProtectIncludedEquipmentSelection}.${poeDomeCameraFree}`) +
          getObjectValue(formFields, `${keyProtectAdditionalEquipment}.${easyPoeTurret}`) +
          getObjectValue(formFields, `${keyProtectAdditionalEquipment}.${easyPoeDomeCamera}`);
      }
    } else if (radioSelection === 'control') {
      if (key === 'onetimePayment') {
        poeCameraCount =
          getObjectValue(formFields, `${keyControlIncludedEquipmentSelection}.${poeTurretCameraFree}`) +
          getObjectValue(formFields, `${keyControlAdditionalEquipment}.${poeTurretCamera}`);
      }
      if (key === 'easyPayment') {
        poeCameraCount =
          getObjectValue(formFields, `${keyControlIncludedEquipmentSelection}.${poeTurretCameraFree}`) +
          getObjectValue(formFields, `${keyControlAdditionalEquipment}.${easyPoeTurret}`);
      }
    }
    return poeCameraCount;
  };

  const countTemperatureSensor = (key) => {
    let tempSensorCount = 0;

    if (radioSelection === 'monitor') {
      if (key === 'onetimePayment') {
        tempSensorCount = getObjectValue(formFields, `${keyMonitorAdditionalEquipment}.${temperatureSensor}`);
      }
      if (key === 'easyPayment') {
        tempSensorCount = getObjectValue(formFields, `${keyMonitorAdditionalEquipment}.${easyTemperatureSensor}`);
      }
      if (key === 'fiveYearEasyPayment') {
        tempSensorCount = getObjectValue(formFields, `${keyMonitorAdditionalEquipment}.${easyFiveYearTemperatureSensor}`);
      }
    }
    if (radioSelection === 'protect') {
      if (key === 'onetimePayment') {
        tempSensorCount = getObjectValue(formFields, `${keyProtectAdditionalEquipment}.${temperatureSensor}`);
      }
      if (key === 'easyPayment') {
        tempSensorCount = getObjectValue(formFields, `${keyProtectAdditionalEquipment}.${easyTemperatureSensor}`);
      }
      if (key === 'fiveYearEasyPayment') {
        tempSensorCount = getObjectValue(formFields, `${keyProtectAdditionalEquipment}.${easyFiveYearTemperatureSensor}`);
      }
    }
    if (radioSelection === 'control') {
      if (key === 'onetimePayment') {
        tempSensorCount = getObjectValue(formFields, `${keyControlAdditionalEquipment}.${temperatureSensor}`);
      }
      if (key === 'easyPayment') {
        tempSensorCount = getObjectValue(formFields, `${keyControlAdditionalEquipment}.${easyTemperatureSensor}`);
      }
      if (key === 'fiveYearEasyPayment') {
        tempSensorCount = getObjectValue(formFields, `${keyControlAdditionalEquipment}.${easyFiveYearTemperatureSensor}`);
      }
    }
    return tempSensorCount;
  };

  const history = useHistory();
  const currentPath = history.location.pathname.split('/')[3];

  return (
    <>
      {title && <EquipmentSectionTitle>{title}</EquipmentSectionTitle>}
      {subTitle && <EquipmentSectionSubTitle>{subTitle}</EquipmentSectionSubTitle>}
      <div style={{ display: 'flex' }}>
        <EquipmentSectionWrapper>
          {oneTimePaymentTitle && (
            <EquipmentSectionSubTitle>
              {oneTimePaymentTitle}
            </EquipmentSectionSubTitle>
          )}
          {formFields[name] &&
            formStepFields.map(({ fields, sys: { id: sysId } }, index) => {
              const key = `EquipmentSection${name}${index}`;
              const error = formErrors[name] && formErrors[name][fields.name];
              const hiddenValue = checkIfHasSwitchOption()
                ? !formFields[name][fields.name].isVisible || formFields[name][fields.name].isHidden
                : false;
              // SOF-1582: Non-zero value for POE switch should not be visible
              const poeCameraCount = countPoeCameras('onetimePayment');
              if (currentPath === 'Order_Details' && formFields[name][poeSwitchNonZero] && poeCameraCount < 5) {
                formFields[name][poeSwitchNonZero].isVisible = false;
              }

              const temperatureSensorCount = countTemperatureSensor('onetimePayment');
              if ((currentPath === 'Quote_tool' || currentPath === 'Order_Details') &&
                formFields[name][temperatureProbe] && temperatureSensorCount < 1) {
                formFields[name][temperatureProbe].isVisible = false;
              }
              const fieldData = {
                ...fields,
                ...formFields[name][fields.name],
                isHidden: hiddenValue,
                formFields,
                sysId,
                onChange: e => handleFormField(e.target)
              };
              return (
                <Fragment key={key}>
                  <FormField data={fieldData} />
                  {error && error.text && <Error text={error.text} />}
                </Fragment>
              );
            })}
        </EquipmentSectionWrapper>

        {/*
        Easy Payments are shown/hidden based on boolean value of shouldHideEasyPayments
      */}
        {(
          <EquipmentSectionWrapper>
            {easyPaymentTitle && (
              <EquipmentSectionSubTitle>
                {easyPaymentTitle}
              </EquipmentSectionSubTitle>
            )}
            {formFields[name] &&
              Array.isArray(easyPaymentFields) &&
              easyPaymentFields.map(({ fields, sys: { id: sysId } }, index) => {
                const key = `EquipmentSection${name}${index}`;
                const error = formErrors[name] && formErrors[name][fields.name];
                const hiddenValue = checkIfHasSwitchOption()
                  ? !formFields[name][fields.name].isVisible || formFields[name][fields.name].isHidden
                  : false;
                // SOF-1582: Non-zero value for POE switch should not be visible
                const poeCameraCount = countPoeCameras('easyPayment');
                if (currentPath === 'Order_Details' && formFields[name][easyPoeSwitchNonZero] && poeCameraCount < 5) {
                  formFields[name][easyPoeSwitchNonZero].isVisible = false;
                }
                const temperatureSensorCount = countTemperatureSensor('easyPayment');
                if ((currentPath === 'Quote_tool' || currentPath === 'Order_Details') &&
                  formFields[name][easyTemperatureProbe] && temperatureSensorCount < 1) {
                  formFields[name][easyTemperatureProbe].isVisible = false;
                }

                const fieldData = {
                  ...fields,
                  ...formFields[name][fields.name],
                  isHidden: hiddenValue,
                  formFields,
                  sysId,
                  onChange: e => handleFormField(e.target)
                };
                return (
                  <Fragment key={key}>
                    <FormField data={fieldData} />
                    {error && error.text && <Error text={error.text} />}
                  </Fragment>
                );
              })}
          </EquipmentSectionWrapper>
        )}

        {/* show five year payment fields */}
        {(
          <EquipmentSectionWrapper>
            {fiveYearEasyPaymentTitle && (
              <EquipmentSectionSubTitle>
                {fiveYearEasyPaymentTitle}
              </EquipmentSectionSubTitle>
            )}
            {formFields[name] &&
              Array.isArray(fiveYearEasyPaymentFields) &&
              fiveYearEasyPaymentFields.map(({ fields, sys: { id: sysId } }, index) => {
                const key = `EquipmentSection${name}${index}`;
                const error = formErrors[name] && formErrors[name][fields.name];
                const hiddenValue = checkIfHasSwitchOption()
                  ? !formFields[name][fields.name].isVisible || formFields[name][fields.name].isHidden
                  : false;
                // SOF-1582: Non-zero value for POE switch should not be visible
                const poeCameraCount = countPoeCameras('easyPayment');
                if (currentPath === 'Order_Details' && formFields[name][easyPoeSwitchNonZero] && poeCameraCount < 5) {
                  formFields[name][easyPoeSwitchNonZero].isVisible = false;
                }
                const easyFiveYearTemperatureSensorCount = countTemperatureSensor('fiveYearEasyPayment');
                if ((currentPath === 'Quote_tool' || currentPath === 'Order_Details') &&
                  formFields[name][easyFiveYearTemperatureProbe] && easyFiveYearTemperatureSensorCount < 1) {
                  formFields[name][easyFiveYearTemperatureProbe].isVisible = false;
                }
                const fieldData = {
                  ...fields,
                  ...formFields[name][fields.name],
                  isHidden: hiddenValue,
                  formFields,
                  sysId,
                  onChange: e => handleFormField(e.target)
                };
                return (
                  <Fragment key={key}>
                    <FormField data={fieldData} />
                    {error && error.text && <Error text={error.text} />}
                  </Fragment>
                );
              })}
          </EquipmentSectionWrapper>
        )}

      </div>
      <Modal
        heading="Alert"
        isOpen={isAlert}
        bodyText={bodyText}
        onClose={() => setIsAlertOpen(false)}
      />
    </>
  );
};

EquipmentSectionForm.propTypes = {
  data: PropTypes.shape({
    formStepFields: PropTypes.arrayOf(PropTypes.object),
    easyPaymentFields: PropTypes.arrayOf(PropTypes.object),
    title: PropTypes.string,
    subTitle: PropTypes.string,
    maxValue: PropTypes.number,
    isLimitCameraOnly: PropTypes.bool,
    name: PropTypes.string.isRequired,
    oneTimePaymentTitle: PropTypes.string.isRequired,
    easyPaymentTitle: PropTypes.string.isRequired
  }).isRequired,
  formErrors: PropTypes.objectOf(PropTypes.object),
  formFields: PropTypes.objectOf(PropTypes.object).isRequired,
  setFormFields: PropTypes.func.isRequired,
  isFormTouched: PropTypes.bool.isRequired,
  setIsFormTouched: PropTypes.func.isRequired,
  equipmentTotalPrice: PropTypes.shape({
    subtractAmountFromPrice: PropTypes.number.isRequired,
    addAmountToPrice: PropTypes.number.isRequired,
    addAmountToMonthly: PropTypes.number.isRequired,
    monthlyPayment: PropTypes.number.isRequired,
    addAmountToFiveYearMonthly: PropTypes.number.isRequired,
    subtractAmountFromFiveYearMonthly : PropTypes.number.isRequired
  }).isRequired,
  setEquipmentTotalPrice: PropTypes.func.isRequired,
  hardwareDiscountAmount: PropTypes.number.isRequired,
  setHardwareDiscountAmount: PropTypes.func.isRequired,
  dueNow: PropTypes.number.isRequired,
  setDueNow: PropTypes.func.isRequired,
  isHardwareDiscount: PropTypes.bool.isRequired
};

EquipmentSectionForm.defaultProps = {
  formErrors: {}
};

export default EquipmentSectionForm;
